/**
 * People IQ — Checklist Template Editor.
 * HR configures which tasks appear when a new employee is put into
 * onboarding / offboarding. Requires `manage_checklist_templates` capability.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, ClipboardCheck, LogOut } from "lucide-react";

const OWNER_OPTS = [
  { value: "hr", label: "HR" },
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "stores", label: "Stores" },
  { value: "it", label: "IT" },
  { value: "accounts", label: "Accounts" },
];

export default function ChecklistTemplates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasCapability(user, "manage_checklist_templates");

  const [onb, setOnb] = useState([]);
  const [off, setOff] = useState([]);
  const [addOpen, setAddOpen] = useState(null); // "onboarding" | "offboarding"
  const [form, setForm] = useState({ key: "", title: "", owner: "hr", mandatory: true, order: 100 });

  const load = async () => {
    const [{ data: a }, { data: b }] = await Promise.all([
      api.get("/people/onboarding-templates"),
      api.get("/people/offboarding-templates"),
    ]);
    setOnb(a); setOff(b);
  };
  useEffect(() => { load(); }, []);

  const addTask = async () => {
    if (!form.key.trim() || !form.title.trim()) return toast.error("Key and title are required");
    if (!/^[a-z0-9_]+$/i.test(form.key)) return toast.error("Key must be alphanumeric + underscore only");
    try {
      const kind = addOpen;
      await api.post(`/people/${kind}-templates`, {
        ...form,
        order: Number(form.order) || 100,
      });
      toast.success("Task added");
      setAddOpen(null);
      setForm({ key: "", title: "", owner: "hr", mandatory: true, order: 100 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const del = async (kind, tid) => {
    if (!window.confirm("Remove this task from the template? Existing onboarding instances are unaffected.")) return;
    try {
      await api.delete(`/people/${kind}-templates/${tid}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const renderTable = (kind, tasks, icon) => (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">{icon} {kind === "onboarding" ? "Onboarding tasks" : "Offboarding tasks"}</CardTitle>
        {canManage && (
          <Button size="sm" className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
                  onClick={() => { setAddOpen(kind); setForm({ key: "", title: "", owner: "hr", mandatory: true, order: (tasks.length + 1) * 10 }); }}
                  data-testid={`add-${kind}-task`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 w-14">#</th>
              <th className="text-left px-4 py-2">Task</th>
              <th className="text-left px-4 py-2 w-32">Owner</th>
              <th className="text-left px-4 py-2 w-24">Mandatory</th>
              <th className="text-right px-4 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody data-testid={`${kind}-templates-body`}>
            {tasks.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-slate-400 text-xs">No tasks defined.</td></tr>
            )}
            {tasks.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-500 font-mono text-xs">{t.order}</td>
                <td className="px-4 py-2">
                  <div className="font-medium text-slate-900">{t.title}</div>
                  <div className="text-[10px] font-mono text-slate-400">{t.key}</div>
                </td>
                <td className="px-4 py-2 text-xs uppercase text-slate-600">{t.owner || "—"}</td>
                <td className="px-4 py-2">
                  {t.mandatory ? <Badge className="bg-rose-100 text-rose-700 text-[10px]">Required</Badge> : <Badge variant="outline" className="text-[10px]">Optional</Badge>}
                </td>
                <td className="px-4 py-2 text-right">
                  {canManage && (
                    <Button variant="ghost" size="icon" onClick={() => del(kind, t.id)} className="text-rose-500 hover:text-rose-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 max-w-6xl" data-testid="templates-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/people")}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-0.5">Checklist Templates</h1>
          <p className="text-sm text-slate-500">Configure the tasks that appear when HR starts onboarding or offboarding.</p>
        </div>
      </div>

      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
        Changes only affect <b>new</b> onboarding / offboarding instances. In-flight checklists keep the task list they were created with.
      </div>

      {renderTable("onboarding", onb, <ClipboardCheck className="w-4 h-4 text-[#7C1D3A]" />)}
      {renderTable("offboarding", off, <LogOut className="w-4 h-4 text-[#7C1D3A]" />)}

      <Dialog open={!!addOpen} onOpenChange={() => setAddOpen(null)}>
        <DialogContent data-testid="tmpl-add-dialog">
          <DialogHeader><DialogTitle>Add {addOpen} task</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <label className="text-[10px] uppercase font-semibold text-slate-500">Title *</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="tmpl-title" placeholder="e.g. Uniform issued" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Key *</label>
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="uniform_issued" data-testid="tmpl-key" className="font-mono" />
              <p className="text-[10px] text-slate-400 mt-0.5">Unique · alphanumeric + underscore.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Owner</label>
              <Select value={form.owner} onValueChange={(v) => setForm({ ...form, owner: v })}>
                <SelectTrigger data-testid="tmpl-owner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OWNER_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Order</label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="tmpl-order" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Mandatory</label>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} data-testid="tmpl-mandatory" /> Required to complete
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(null)}>Cancel</Button>
            <Button onClick={addTask} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="tmpl-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
